import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { useCurrency } from '../../contexts/CurrencyContext';
import { formatChile } from '../../utils/dateUtils';
import {
  Crown, Check, X, Loader2, ArrowUpRight, ArrowDownRight,
  RefreshCw, History, Zap, ChevronRight, Calendar, Star
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_ICONS = {
  basic: null,
  pro: Zap,
  premium: Crown,
};

const PLAN_COLORS = {
  basic: { bg: 'bg-slate-50', border: 'border-slate-200', accent: 'text-slate-600', badge: 'bg-slate-100 text-slate-700' },
  pro: { bg: 'bg-blue-50', border: 'border-blue-300', accent: 'text-blue-600', badge: 'bg-blue-100 text-blue-700' },
  premium: { bg: 'bg-amber-50', border: 'border-amber-300', accent: 'text-amber-600', badge: 'bg-amber-100 text-amber-700' },
};

const CHANGE_TYPE_MAP = {
  upgrade: { label: 'Upgrade', icon: ArrowUpRight, color: 'text-green-600 bg-green-50' },
  downgrade: { label: 'Downgrade', icon: ArrowDownRight, color: 'text-orange-600 bg-orange-50' },
  renewal: { label: 'Renovación', icon: RefreshCw, color: 'text-blue-600 bg-blue-50' },
  initial: { label: 'Activación', icon: Check, color: 'text-purple-600 bg-purple-50' },
};

export const MembershipTab = ({ membership, plans, onPlanChanged }) => {
  const { formatPrice } = useCurrency();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (membership?.billing_cycle) {
      setBillingCycle(membership.billing_cycle);
    }
  }, [membership]);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API_URL}/api/memberships/history`);
      setHistory(res.data);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const toggleHistory = () => {
    if (!showHistory && history.length === 0) {
      fetchHistory();
    }
    setShowHistory(!showHistory);
  };

  const getPlanDisplayPrice = (plan) => {
    return billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
  };

  const fmtPrice = (amount) => {
    if (amount === 0) return 'Gratis';
    return formatPrice(amount, 'CLP') || `$${amount.toLocaleString('es-CL')}`;
  };

  const getPlanNameForLog = (planKey) => {
    const found = plans.find(p => p.plan_key === planKey);
    return found ? found.name : planKey;
  };

  const getEventsLabel = (plan) => {
    if (plan.max_events === -1) return 'Publicación ilimitada de eventos';
    return `Hasta ${plan.max_events} eventos`;
  };

  const getFeaturedLabel = (plan) => {
    if (plan.max_featured_events === -1) return 'Eventos destacados ilimitados';
    if (plan.max_featured_events === 0) return null;
    return `${plan.max_featured_events} eventos destacados`;
  };

  return (
    <div className="space-y-6" data-testid="membership-tab">
      {/* Current Plan Banner */}
      {membership && (
        <Card className="rounded-2xl border-0 shadow-sm overflow-hidden" data-testid="current-plan-banner">
          <div className="bg-gradient-to-r from-[#064E3B] to-[#0D7354] p-6 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-green-200 text-sm font-medium mb-1">Tu plan actual</p>
                <h3 className="text-2xl font-bold flex items-center gap-2">
                  <Crown className="w-6 h-6 text-yellow-300" />
                  {membership.plan_name}
                </h3>
                <div className="flex items-center gap-3 mt-2">
                  <Badge className="bg-white/15 text-white border-0 backdrop-blur-sm">
                    {billingCycle === 'yearly' ? 'Anual' : 'Mensual'}
                  </Badge>
                  {membership.is_active && (
                    <Badge className="bg-green-400/20 text-green-200 border-0">
                      Activo
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-right">
                {membership.end_date && (
                  <div>
                    <p className="text-green-200 text-sm">Válido hasta</p>
                    <p className="font-semibold text-lg">{formatChile(membership.end_date, "d MMM yyyy")}</p>
                  </div>
                )}
                <p className="text-green-200 text-sm mt-1">
                  {membership.price_monthly === 0 && membership.price_yearly === 0
                    ? 'Gratis'
                    : billingCycle === 'yearly'
                      ? fmtPrice(membership.price_yearly) + '/año'
                      : fmtPrice(membership.price_monthly) + '/mes'}
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Admin-managed notice */}
      <Card className="rounded-2xl border-2 border-amber-200 bg-amber-50 shadow-sm" data-testid="admin-managed-notice">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-100 rounded-xl flex-shrink-0">
              <Crown className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-[#064E3B] text-lg mb-1">Gestión de planes</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Los cambios de plan son gestionados por el equipo de PlanCity.
                Si deseas cambiar tu plan actual, por favor contacta al administrador.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href="https://wa.me/56990934576?text=Hola%2C%20me%20gustar%C3%ADa%20cambiar%20mi%20plan%20en%20PlanCity"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white rounded-full text-sm font-medium hover:bg-[#1DA851] transition-colors"
                  data-testid="contact-admin-btn"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  Contactar por WhatsApp
                </a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plans overview (read-only) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="plans-grid">
        {plans.map((plan) => {
          const isCurrent = membership?.plan === plan.plan_key;
          const colors = PLAN_COLORS[plan.plan_key] || PLAN_COLORS.basic;
          const PlanIcon = PLAN_ICONS[plan.plan_key];
          const price = getPlanDisplayPrice(plan);
          const featuredLabel = getFeaturedLabel(plan);

          return (
            <Card
              key={plan.plan_key}
              data-testid={`plan-card-${plan.plan_key}`}
              className={`rounded-2xl transition-all duration-200 ${
                isCurrent
                  ? `border-2 ${colors.border} ${colors.bg} shadow-md`
                  : 'border border-gray-100 opacity-60'
              }`}
            >
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {PlanIcon && <PlanIcon className={`w-5 h-5 ${colors.accent}`} />}
                    <CardTitle className="text-lg text-[#064E3B]">{plan.name}</CardTitle>
                  </div>
                  {isCurrent && (
                    <Badge className={`${colors.badge} border-0 text-xs`} data-testid={`current-badge-${plan.plan_key}`}>
                      Plan actual
                    </Badge>
                  )}
                </div>
                <CardDescription className="text-sm">{plan.description}</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Price */}
                <div className="mb-5">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-[#064E3B]">
                      {price === 0 ? 'Gratis' : fmtPrice(price)}
                    </span>
                    {price > 0 && (
                      <span className="text-gray-500 text-sm">
                        /{billingCycle === 'yearly' ? 'año' : 'mes'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Limits */}
                <div className="space-y-2 mb-3 pb-3 border-b border-gray-100">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-[#00CC66] flex-shrink-0" />
                    <span className="text-gray-700">{getEventsLabel(plan)}</span>
                  </div>
                  {featuredLabel && (
                    <div className="flex items-center gap-2 text-sm">
                      <Star className="w-4 h-4 text-[#00CC66] flex-shrink-0" />
                      <span className="text-gray-700">{featuredLabel}</span>
                    </div>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      {feature.is_included ? (
                        <Check className="w-4 h-4 text-[#00CC66] mt-0.5 flex-shrink-0" />
                      ) : (
                        <X className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" />
                      )}
                      <span className={feature.is_included ? 'text-gray-700' : 'text-gray-400'}>
                        {feature.name}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Status label (no action button) */}
                <div className={`w-full text-center py-2 rounded-xl text-sm font-medium ${
                  isCurrent ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                }`}>
                  {isCurrent ? 'Tu plan actual' : 'Contacta al admin'}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* History Toggle */}
      <div className="flex justify-center">
        <Button
          variant="outline"
          className="rounded-full"
          onClick={toggleHistory}
          data-testid="toggle-history-btn"
        >
          <History className="w-4 h-4 mr-2" />
          {showHistory ? 'Ocultar historial' : 'Ver historial de cambios'}
        </Button>
      </div>

      {/* History Section */}
      {showHistory && (
        <Card className="rounded-2xl border-0 shadow-sm" data-testid="plan-history">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <History className="w-5 h-5" />
              Historial de cambios
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8">
                <History className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 text-sm">No hay cambios de plan registrados</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((log) => {
                  const typeInfo = CHANGE_TYPE_MAP[log.change_type] || CHANGE_TYPE_MAP.initial;
                  const TypeIcon = typeInfo.icon;
                  return (
                    <div
                      key={log.id}
                      className="flex items-center gap-4 p-3 rounded-xl bg-gray-50"
                      data-testid={`history-entry-${log.id}`}
                    >
                      <div className={`p-2 rounded-lg ${typeInfo.color}`}>
                        <TypeIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#064E3B]">
                          {log.from_plan ? (
                            <>
                              {getPlanNameForLog(log.from_plan)}
                              <span className="text-gray-400 mx-2">&rarr;</span>
                              {getPlanNameForLog(log.to_plan)}
                            </>
                          ) : (
                            <>Activación: {getPlanNameForLog(log.to_plan)}</>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {log.billing_cycle === 'yearly' ? 'Anual' : 'Mensual'}
                          {' · '}
                          {log.changed_at && formatChile(log.changed_at, "d MMM yyyy 'a las' HH:mm")}
                        </p>
                      </div>
                      <Badge className={`${typeInfo.color} border-0 text-xs`}>
                        {typeInfo.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
